<template>
    <div class="course__edit_details">
        <form-text
            :disabled="isShowMode || isRouteCatalogItem"
            :name="'course-edit-name'"
            :label="'Name'"
            :required="true"
            :v-validate="'required|max:200'"
            :data-vv-as="'name'"
            v-model="courseName"
        />
        <form-editor
            :disabled="isShowMode || isRouteCatalogItem"
            :name="'course-edit-description'"
            :label="'Description'"
            ref="formeditor1"
            v-model="courseDescription"
        />
        <form-time
            :disabled="isShowMode || isRouteCatalogItem"
            :label="'Duration'"
            :seconds="true"
            :name="'course-edit-details-duration'"
            :value="courseDuration"
            :v-validate="{ date_format: 'HH:mm:ss' }"
            :placeholder="'00:00:00'"
            :data-vv-as="'duration'"
            @input="updateCourseDuration"
        ></form-time>
        <form-select
            :disabled="isShowMode || isRouteCatalogItem"
            :data-vv-as="'labels'"
            :get-pagination-getter="'getEbsCourseCustomLabelsPagination'"
            :get-items-getter="'getEbsCourseCustomLabelsMappedIdNameStatus'"
            :get-items-getter-mapped="'getEbsCourseCustomLabelsMappedLabelValue'"
            :get-items-dispatch="'ebsCourseGetLabels'"
            :item-name="['label', 'labels']"
            :label="'Labels'"
            :name="'module-edit-details-labels'"
            :multiple="true"
            :search-queryable="true"
            :table-labels="[
                {
                    key: 'name',
                    title: 'Name',
                    type: 'string'
                }
            ]"
            :items-extra="customCourseLabels"
            :hint-button="!isShowMode && !isRouteCatalogItem"
            :value="courseLabels"
            @input="updateCourseLabels"
            @input-table="updateCourseLabels"
            @emit-hint="switchCustomLabelField"
        >{{ !isShowMode && !isRouteCatalogItem ? 'Not found the label? Click to add new one' : '' }}</form-select>
        <template v-if="customLabelFieldEnabled">
            <component-wrapper
                :class="'course__edit_details_customlabel'"
                :overlay="true"
            >
                <div class="columns">
                    <div class="column">
                        <form-text
                            :name="'course-edit-details-custom-label'"
                            :label="''"
                            :value="customLabel"
                            :hint-position-top="true"
                            v-model="customLabel"
                        >Type custom label name</form-text>
                    </div>
                    <div class="column is-narrow">
                        <component-button
                            :theme="'default'"
                            :icon="true"
                            :icon-name="'check'"
                            :icon-align="'right'"
                            :name="'course-edit-details-custom-label-accept'"
                            @click="submitCustomLabel"
                        >Accept</component-button>
                    </div>
                </div>
            </component-wrapper>
        </template>
        <form-image
            :disabled="isShowMode || isRouteCatalogItem"
            :name="'course-edit-image'"
            :label="'Image'"
            :value="courseImage"
            @emit-select="courseSetImages"
            @emit-remove="courseRemoveImages"
        />
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'OK'"
            :submit-button-active="false"
        />
    </div>
</template>

<script>
import { validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'
import FormText from '@/components/default/forms/FormText'
import FormImage from '@/components/default/forms/FormImage'
import PopNotify from '@/components/default/shared/PopNotify'
import FormTime from '@/components/default/forms/FormTime'
import FormSelect from '@/components/default/forms/FormSelect'
import ComponentWrapper from '@/components/default/shared/ComponentWrapper'
import ComponentButton from '@/components/default/shared/ComponentButton'

export default {
  name: 'course-edit-details',
  components: {
    ComponentButton,
    ComponentWrapper,
    FormSelect,
    FormTime,
    PopNotify,
    FormImage,
    FormEditor: () => import('@/components/default/forms/FormEditor'),
    FormText
  },
  data () {
    return {
      customLabelFieldEnabled: false,
      customLabel: ''
    }
  },
  mixins: [validateBeforeRouteLeave, watchContentChangeEmit],
  computed: {
    courseId: {
      get () {
        return this.$store.getters.getEbsCourseId
      }
    },
    courseName: {
      get () {
        return this.$store.getters.getEbsCourseName
      },
      set (value) {
        this.$store.dispatch('ebsCourseSetName', value)
      }
    },
    courseDuration: {
      get () {
        return this.$store.getters.getEbsCourseDuration
      }
    },
    courseDescription: {
      get () {
        return this.$store.getters.getEbsCourseDescription
      },
      set (value) {
        this.$store.dispatch('ebsCourseSetDescription', value)
      }
    },
    courseImage: {
      get () {
        return this.$store.getters.getEbsCourseFile
      }
    },
    courseLabels () {
      return this.$store.getters.getEbsCourseLabels
    },
    courseLabelsNew () {
      return this.$store.getters.getEbsCourseLabelsNew
    },
    customCourseLabels () {
      return this.courseLabelsNew.length ? this.courseLabelsNew.filter(fObj => fObj.constructor === String).map(mObj => {
        return {
          label: mObj,
          value: mObj
        }
      }) : []
    },
    watchContent () {
      return this.courseName + this.courseDescription + this.courseImage
    },
    //
    rootItemName () {
      return 'course'
    },
    //
    isAddMode () {
      return this.$route.name.includes('course-add')
    },
    isEditMode () {
      return this.$route.name.includes('course-edit')
    },
    isShowMode () {
      return this.$route.name.includes('course-show')
    },
    isRouteCatalogItem () {
      return this.$route.name.includes('ebs-catalog-item-edit')
    }
  },
  methods: {
    courseSetImages (data) {
      this.$store.dispatch('ebsCourseSetImage', data)
    },
    courseRemoveImages (key) {
      this.$store.dispatch('ebsCourseRemoveImage', key)
    },
    updateCourseDuration (data) {
      this.$store.dispatch('ebsCourseSetDuration', data)
    },
    updateCourseLabels (data) {
      this.$store.dispatch('ebsCourseSetLabels', data)
    },
    switchCustomLabelField (data) {
      this.customLabelFieldEnabled = true
    },
    submitCustomLabel () {
      this.$store.dispatch('ebsCourseAddLabel', this.customLabel)
      this.customLabelFieldEnabled = false
      this.customLabel = ''
    }
  },
  inject: ['$validator']
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/ebs/courses/courseaddeditdetails";
</style>
